import React, { useEffect, useMemo, useState } from "react";
import {
  FlatList,
  Image,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { ProgressBar, Searchbar, Text } from "react-native-paper";
import { HistoryResource } from "./resources/History.resource";
import { ParcelViewModel } from "@viewModels/Parcel.viewModel";
import { theme } from "@config/Theme";
import { useEvent } from "@events/hooks/useEvent";
import { EventTypes } from "@events/Event.types";
import { ParcelCardHeader } from "@components/recipient-parcel/ParcelCardHeader";
import { Card } from "@components/Card";
import { useTranslation } from "react-i18next";
import { CustomButtom } from "@components/CustomButton";
import UserParcelsEmpty from "@assets/img/user-parcels-empty.png";

interface Props {
  goToDelivery: (id: string, phone: string) => void;
}

export const HistoryFeature = ({ goToDelivery }: Props) => {
  const { t } = useTranslation("recipient");

  // states
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [parcels, setParcels] = useState<ParcelViewModel[]>([]);
  const [hasMoreData, setHasMoreData] = useState<boolean>(true);

  const groupedParcelsByDate = useMemo(() => {
    if (parcels.length === 0) {
      return;
    }
    return parcels
      .filter((p) => p.recipient !== null)
      .filter(
        (p) =>
          p.EAN?.includes(searchQuery) ||
          (p.recipient?.profile.name &&
            p.recipient?.profile.name.includes(searchQuery)) ||
          p.recipient?.profile.phone.includes(searchQuery)
      )
      .reduce((acc, delivery) => {
        const date = delivery.updatedAtDate.toFormat("dd LLL yyyy");
        // @ts-ignore
        if (!acc[date]) {
          // @ts-ignore
          acc[date] = [];
        }
        // @ts-ignore
        acc[date].push(delivery);
        return acc;
      }, {}) as unknown as { [key: string]: ParcelViewModel[] };
  }, [parcels, searchQuery]);

  // Functions
  async function getFirstChunkOfData() {
    setPageNumber(0);
    try {
      setIsLoading(true);
      const response = await HistoryResource.list(0);
      setParcels(response);
      setPageNumber(1);
      setHasMoreData(response.length >= 10);
    } catch (error) {
      setParcels([]);
    } finally {
      setIsLoading(false);
    }
  }

  async function loadMoreData() {
    if (!hasMoreData || isLoading) return;
    try {
      setIsLoading(true);
      // fetch the next chunck of data
      const response = await HistoryResource.list(pageNumber);
      const data = [...parcels, ...response];
      setParcels(data);
      setPageNumber(pageNumber + 1);
      setHasMoreData(response.length >= 10);
    } catch (err) {
      __DEV__ && console.log("error while loading more data", err);
    } finally {
      setIsLoading(false);
    }
  }

  function onItemClick(delivery: ParcelViewModel) {
    const phone = delivery.recipient?.profile.phone ?? null;
    if (phone === null) return;
    goToDelivery(delivery.id, phone);
  }

  // effects
  useEffect(() => {
    getFirstChunkOfData();
  }, []);

  useEvent(
    [
      EventTypes.PARCEL.COLLECT,
      EventTypes.PARCEL.CANCELLED,
      EventTypes.PARCEL.INVALID_COUNTER,
      EventTypes.PARCEL.VALID,
      EventTypes.PARCEL.UPDATED,
    ],
    () => {
      getFirstChunkOfData();
    }
  );

  // rendering component
  if (isLoading && pageNumber === 0) {
    return <ProgressBar indeterminate />;
  }

  return (
    <>
      <View style={styles.searchBarWrapper}>
        <Searchbar
          value={searchQuery}
          onChangeText={setSearchQuery}
          style={styles.searchBar}
          placeholder={t("history_feature_searchBar_placeHolder")!}
          placeholderTextColor={theme.colors.onBackground}
        />
      </View>
      {parcels.length === 0 || !groupedParcelsByDate ? (
        <View style={{ flex: 1, justifyContent: "flex-end" }}>
          <View style={styles.emptyView}>
            <View>
              <Text variant="bodyMedium" style={{ paddingHorizontal: 20 }}>
                {t("history_feature_empty_content")!}
              </Text>
            </View>
            {searchQuery === "" && (
              <View style={{ paddingTop: 20 }}>
                <CustomButtom
                  onPress={() => getFirstChunkOfData()}
                  text={t("history_feature_empty_button")!}
                  loading={isLoading}
                />
              </View>
            )}
            <Image
              source={UserParcelsEmpty}
              style={styles.userParcelsEmptyImage}
            />
          </View>
        </View>
      ) : (
        <FlatList
          data={Object.keys(groupedParcelsByDate)}
          keyExtractor={(item) => item}
          refreshing={isLoading}
          onRefresh={async () => await getFirstChunkOfData()}
          onEndReached={loadMoreData}
          onEndReachedThreshold={0.95}
          renderItem={({ item }) => {
            return (
              <View key={item} style={{ marginTop: 20 }}>
                {/*date */}
                <Text style={styles.dateStyle}>{item}</Text>
                {/*List of parcels */}
                <View style={{ paddingHorizontal: 20 }}>
                  {groupedParcelsByDate[item].map((delivery) => (
                    <TouchableOpacity
                      onPress={() => onItemClick(delivery)}
                      style={{ marginBottom: 10 }}
                      key={delivery.id}
                    >
                      <Card>
                        <ParcelCardHeader
                          key={delivery.recipient!.id}
                          title={delivery.EAN}
                          parcelStatus={delivery.status}
                        />
                      </Card>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            );
          }}
          style={styles.listStyle}
          contentContainerStyle={styles.listContentStyle}
          scrollEnabled={true}
          showsVerticalScrollIndicator={false}
        ></FlatList>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  searchBarWrapper: {
    paddingHorizontal: 20,
    paddingVertical: 10,
  },
  searchBar: {
    borderRadius: 10,
    backgroundColor: "white",
    borderWidth: 1,
    borderColor: theme.colors.gray,
  },
  emptyView: {
    flex: 1,
    justifyContent: "space-between",
    paddingTop: 20,
  },
  userParcelsEmptyImage: {
    bottom: 0,
    width: "100%",
    height: "100%",
    resizeMode: "contain",
  },

  dateStyle: {
    fontWeight: "bold",
    paddingLeft: 20,
    paddingBottom: 15,
  },

  listContentStyle: {
    paddingBottom: 50,
  },
  listStyle: {
    flex: 1,
  },
});
